import { Box, Flex, Heading, Icon, Text } from "@chakra-ui/react";
import { type ChainFullId, ChainFullIds, formatCurrency, parseToNormalUnit } from "@umami/chains";
import { useGetAccountBalance, useGetAssetPrice, useGetNetworkSymbol } from "@umami/state";
import { type BigNumber } from "bignumber.js";

import { TezIcon } from "../../assets/icons";
import { useColor } from "../../styles/useColor";

export type TezTileProps = {
  address?: string;
  mutezAmount?: string | number | BigNumber;
  chainId?: ChainFullId;
};

export const TezTile = ({ address, mutezAmount, chainId = ChainFullIds.TEZOS }: TezTileProps) => {
  const color = useColor();
  const getBalance = useGetAccountBalance();
  const getAssetPrice = useGetAssetPrice();
  const symbol = useGetNetworkSymbol()(chainId);

  const getCurrentBalance = () => {
    if (mutezAmount !== undefined) {
      return mutezAmount;
    }

    if (address) {
      return getBalance(address);
    }
  };

  const getFormattedBalance = () => {
    const currentBalance = getCurrentBalance();
    const formattedBalance =
      currentBalance !== undefined ? parseToNormalUnit(currentBalance.toString(), chainId) : "0.00";

    return `${formattedBalance} ${symbol}`;
  };

  const usdBalance = formatCurrency(
    getAssetPrice(symbol) *
      Number(parseToNormalUnit(getCurrentBalance()?.toString() ?? "0", chainId))
  );

  return (
    <Box padding="16px" background={color("100")} borderRadius="6px">
      <Flex alignItems="center">
        <Icon
          as={TezIcon}
          boxSize="42px"
          marginRight="16px"
          borderRadius="6px"
          data-testid="tez-icon"
        />
        <Box>
          <Heading color={color("900")} size="md">
            {getFormattedBalance()}
          </Heading>
          <Text color={color("700")} data-testid="tez-dollar-amount" size="sm">
            {usdBalance}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};
